import React from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { ContentTransition } from '../../route-transition/route-transition';
import { IndicatorGroup } from '../../../motion';
import { BackBar, BackButton } from '../../layout/back-button';
import { useAuth } from '../../../context/auth-provider';
import { resolvePostAuthRoute } from '../../../utils/post-auth-route';
import { LogoMark } from '../../logo/logo-mark';
import {
  Shell,
  Card,
  BrandRow,
  Wordmark,
  Heading,
  Subheading,
} from '../../onboarding/onboarding.styled';
import { NotificationPreferences } from '../../notifications/notification-preferences';
import { SessionsPanel } from '../../auth/sessions-panel';
import { AccountProfile } from './account-profile';
import { AccountData } from './account-data';
import { AccountPassword } from './account-password';
import { AccountPrivacy } from './account-privacy';
import { AccountMfa } from './account-mfa';
import { AccountLegal } from './account-legal';
import { AccountOrganization } from './account-organization';
import { can, PERMISSIONS } from '../../../auth/permissions';
import {
  DevicesWrap,
  SectionHeading,
  SectionLead,
  Tab,
  TabIndicator,
  TabLabel,
  Tabs,
} from './account.styled';

/**
 * THE ACCOUNT AREA — one page, four sections, four URLs.
 *
 * ── WHAT THIS REPLACED, AND WHY ──
 * /account used to be a single screen titled "Change your password", with the
 * notification switches bolted onto the bottom of it. Two things followed from
 * that, and both were reported as bugs:
 *
 *   1. There was NO WAY TO EDIT YOUR ACCOUNT. The account menu's entries were
 *      Main Menu, Change Password, Devices and Sign Out. A name or phone number
 *      mistyped at signup — or typed for you by whoever sent your invitation —
 *      was permanent, for every role.
 *
 *   2. CLICKING A NOTIFICATION OPENED A PASSWORD FORM. Six event types in the
 *      backend registry deep-linked to '/account', so "somebody accepted your
 *      invitation" and "a payment was declined" both landed on a page about
 *      passwords, with a notification-preferences panel underneath — the one
 *      screen a notification should never open, because reading a notification
 *      and changing which notifications you receive are opposite intents.
 *
 * Giving each concern its own route is what lets a notification, an email
 * footer and a menu item each point at the one they actually mean.
 *
 * ── THE GATE IS "SIGNED IN", AND NOTHING ELSE ──
 * Registered with NO entry in ROUTE_ROLES or ROUTE_PERMISSIONS, deliberately.
 * Every section here is about the CALLER'S OWN account: a concierge rotating
 * their password, a resident correcting their surname and an owner muting a
 * feed are the same class of act, and none of them is a staff capability. The
 * backend holds the same line — /profile and /password/change are mounted
 * before the staff-only gate.
 */

/**
 * ── EVERY LINK AND REDIRECT IN HERE IS ABSOLUTE, AND HAS TO BE ──
 *
 * This page is mounted at '/account/*' and App.tsx opts the whole router into
 * `v7_relativeSplatPath`. Under that flag a RELATIVE path inside a splat route
 * resolves against the FULL matched location instead of the part before the
 * splat — so the tabs' `to="password"`, pressed at /account/profile, resolved
 * to /account/profile/password.
 *
 * Nothing matched it, so the catch-all below fired, and the catch-all was
 * relative too: /account/profile/password/profile, which matched nothing
 * either. Every tab press therefore either did nothing visible or spun. Proven
 * against react-router 6.30 by clicking the same link with the flag on and off:
 * off -> /account/password, on -> /account/profile/password.
 *
 * An absolute path resolves identically under both behaviours, which is why the
 * fix is to state the destination rather than to turn the flag off — the flag is
 * the v7 behaviour this app has already opted into everywhere else.
 */
const ACCOUNT_BASE = '/account';

interface SectionDef {
  path: string;
  label: string;
  element: React.ReactNode;
  /* When set, the tab and its route exist only for people holding it. Every
     other section is about the caller's own account and needs none. */
  permission?: string;
}

/** The section's real URL. One place, so a tab and a redirect cannot disagree. */
const sectionHref = (path: string): string => `${ACCOUNT_BASE}/${path}`;

const AccountDevices: React.FC = () => (
  <>
    <SectionHeading>Signed-in devices</SectionHeading>
    <SectionLead>
      Every browser and phone that can currently reach your account. Sign out
      anything you do not recognise — if something here is a surprise, change
      your password too.
    </SectionLead>
    <DevicesWrap>
      <SessionsPanel />
    </DevicesWrap>
  </>
);

const AccountNotifications: React.FC = () => (
  <>
    <SectionHeading>Notifications</SectionHeading>
    <SectionLead>
      Choose how you hear about each thing. Turning a channel off stops the
      delivery, not the record — anything switched off still appears in your
      bell’s history, it just won’t count as unread, email you, or buzz your
      phone.
    </SectionLead>
    <NotificationPreferences />
  </>
);

const SECTIONS: SectionDef[] = [
  { path: 'profile', label: 'Details', element: <AccountProfile /> },
  { path: 'password', label: 'Password', element: <AccountPassword /> },
  /* Two-step sign-in: every account, the holder's own choice (migration 103). */
  { path: 'security', label: 'Two-step sign-in', element: <AccountMfa /> },
  { path: 'devices', label: 'Devices', element: <AccountDevices /> },
  {
    path: 'notifications',
    label: 'Notifications',
    element: <AccountNotifications />,
  },
  /* Export and closure. A fifth URL rather than a footer on the password form:
     both are things a person comes here specifically to do, and neither should
     be found by scrolling past something else. */
  { path: 'data', label: 'Your data', element: <AccountData /> },
  /* The one section that is NOT about the caller: the owner answering a
     data-subject request for anyone in the building's records. Owner only, so
     for everyone else neither the tab nor the route exists — a typed URL falls
     through to the catch-all and lands on Details. */
  /* The company's name (Plan 3 F2). Owner only, like the privacy requests:
     billing:manage is the account holder's alone, and so is the name. */
  {
    path: 'company',
    label: 'Company',
    element: <AccountOrganization />,
    permission: PERMISSIONS.BILLING_MANAGE,
  },
  {
    path: 'privacy',
    label: 'Privacy requests',
    element: <AccountPrivacy />,
    permission: PERMISSIONS.PRIVACY_REQUESTS,
  },
  /* The policies, for every role. Last: it is reference reading, not a task. */
  { path: 'legal', label: 'Legal', element: <AccountLegal /> },
];

/* WHICH LAYER A LOCATION BELONGS TO, for the content transition. A section's
   own path is its key. A bare /account and an unknown sub-path both REDIRECT
   to Details, so they are keyed as Details already: the redirect then swaps
   the route inside one layer instead of playing an exit for a screen that was
   never going to be shown. */
const sectionKeyFor = (pathname: string, sections: SectionDef[]): string => {
  const segment = pathname.split('/')[2] || '';

  return sections.some(section => section.path === segment)
    ? segment
    : 'profile';
};

export const AccountPage: React.FC = () => {
  const { user, onboarding, permissions } = useAuth();
  /* Read here and handed to <Routes>: the leaving section keeps rendering for
     its exit, and pinning the location is what stops it from matching the
     arriving route mid-animation (see route-transition.tsx). */
  const location = useLocation();
  const sections = SECTIONS.filter(
    section => !section.permission || can(permissions, section.permission)
  );
  const sectionKey = sectionKeyFor(location.pathname, sections);

  return (
    /* NO <main> AND A DEAD SKIP LINK, both measured on /account/profile. Fixed
       in the Shell itself — see onboarding.styled.tsx — because eight other
       screens are built on it and had the same hole. */
    <Shell>
      <Card wide>
        {/* TOP LEFT. This was an anchor styled as body copy at the BOTTOM
            of the card, under the tab content — so on the Devices tab, which
            is long, the only way back was off-screen. */}
        <BackBar>
          <BackButton
            to={resolvePostAuthRoute(user, onboarding, permissions)}
            label="Back to Lobbero"
          />
        </BackBar>
        <BrandRow>
          <LogoMark size={40} aria-hidden="true" />
          <Wordmark>Lobbero</Wordmark>
        </BrandRow>
        <Heading>Your account</Heading>
        <Subheading>
          {user?.email
            ? `Signed in as ${user.email}.`
            : 'Your details, your password, your devices and what Lobbero tells you.'}
        </Subheading>

        <Tabs aria-label="Account sections">
          {/* The active pill is ONE element that slides between tabs. NavLink's
              render-prop tells each tab whether it is current, so the pill is
              rendered inside exactly one of them and framer carries it over. */}
          <IndicatorGroup id="account-tabs">
            {sections.map(section => (
              <Tab key={section.path} to={sectionHref(section.path)}>
                {({ isActive }) => (
                  <>
                    {isActive && (
                      <TabIndicator
                        id="account-tabs"
                        data-active-indicator="true"
                      />
                    )}
                    <TabLabel>{section.label}</TabLabel>
                  </>
                )}
              </Tab>
            ))}
          </IndicatorGroup>
        </Tabs>

        <ContentTransition contentKey={sectionKey} direction="none">
        <Routes location={location}>
          {/* A bare /account is the details form, which is what "edit my
              account" means to somebody who arrived from the menu. It redirects
              rather than rendering in place so the tab highlight and the URL
              agree — a page that shows Details while the address bar says
              nothing about it cannot be linked to or bookmarked. */}
          <Route
            index
            element={<Navigate to={sectionHref('profile')} replace />}
          />
          {sections.map(section => (
            <Route
              key={section.path}
              path={section.path}
              element={section.element}
            />
          ))}
          {/* Anything else under /account is a stale or mistyped link — most
              likely an old notification pointing at a section that has been
              renamed. Land on Details rather than on a blank card.

              ABSOLUTE, and this one is not a nicety: a RELATIVE redirect here
              appends to a path that already failed to match, so it produces
              another path that cannot match, and redirects again. That is the
              loop the tabs fell into. */}
          <Route
            path="*"
            element={<Navigate to={sectionHref('profile')} replace />}
          />
        </Routes>
        </ContentTransition>
      </Card>
    </Shell>
  );
};
